import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    console.log('[FRONTEND] [AUTH_SLICE] ===== LOGIN THUNK STARTED =====');
    console.log('[FRONTEND] [AUTH_SLICE] Login attempt initiated');
    console.log('[FRONTEND] [AUTH_SLICE] Timestamp:', new Date().toISOString());
    console.log('[FRONTEND] [AUTH_SLICE] Credentials received:', {
      email: credentials.email,
      hasPassword: !!credentials.password,
      passwordLength: credentials.password ? credentials.password.length : 0
    });
    
    try {
      console.log('\n' + '='.repeat(80));
      console.log('[FRONTEND] [AUTH_SLICE] ===== PREPARING POST REQUEST =====');
      console.log('[FRONTEND] [AUTH_SLICE] Timestamp:', new Date().toISOString());
      console.log('[FRONTEND] [AUTH_SLICE] API endpoint: /auth/login');
      console.log('[FRONTEND] [AUTH_SLICE] API baseURL:', api.defaults.baseURL);
      console.log('[FRONTEND] [AUTH_SLICE] Full URL will be:', api.defaults.baseURL + '/auth/login');
      console.log('[FRONTEND] [AUTH_SLICE] Request method: POST');
      console.log('[FRONTEND] [AUTH_SLICE] Credentials to send:', {
        email: credentials.email,
        hasPassword: !!credentials.password,
        passwordLength: credentials.password ? credentials.password.length : 0,
        passwordPreview: credentials.password ? credentials.password.substring(0, 1) + '***' : 'empty'
      });
      console.log('[FRONTEND] [AUTH_SLICE] Axios instance config:', {
        baseURL: api.defaults.baseURL,
        timeout: api.defaults.timeout,
        headers: api.defaults.headers
      });
      console.log('[FRONTEND] [AUTH_SLICE] About to call: api.post("/auth/login", credentials)');
      console.log('[FRONTEND] [AUTH_SLICE] ===== CALLING POST REQUEST NOW =====\n');
      
      const response = await api.post('/auth/login', credentials);
      
      console.log('\n' + '='.repeat(80));
      console.log('[FRONTEND] [AUTH_SLICE] ===== POST REQUEST COMPLETED =====');
      console.log('[FRONTEND] [AUTH_SLICE] Request was successful!');
      
      console.log('[FRONTEND] [AUTH_SLICE] ===== API RESPONSE RECEIVED =====');
      console.log('[FRONTEND] [AUTH_SLICE] Response status:', response.status);
      console.log('[FRONTEND] [AUTH_SLICE] Response headers:', response.headers);
      console.log('[FRONTEND] [AUTH_SLICE] Response data keys:', Object.keys(response.data || {}));
      
      const { token, user } = response.data;
      
      console.log('[FRONTEND] [AUTH_SLICE] Token extracted:', token ? 'Yes' : 'No');
      console.log('[FRONTEND] [AUTH_SLICE] Token length:', token ? token.length : 0);
      console.log('[FRONTEND] [AUTH_SLICE] User data extracted:', user ? 'Yes' : 'No');
      
      if (user) {
        console.log('[FRONTEND] [AUTH_SLICE] User details:', {
          id: user.id,
          email: user.email,
          role: user.role,
          name: user.name,
          isActive: user.isActive
        });
      }
      
      console.log('[FRONTEND] [AUTH_SLICE] Saving token to localStorage...');
      localStorage.setItem('token', token);
      // Store login timestamp for session timeout (60 minutes)
      localStorage.setItem('loginTimestamp', Date.now().toString());
      console.log('[FRONTEND] [AUTH_SLICE] Token saved to localStorage');
      console.log('[FRONTEND] [AUTH_SLICE] Login timestamp saved for session timeout');
      console.log('[FRONTEND] [AUTH_SLICE] Verifying token in localStorage:', !!localStorage.getItem('token'));
      console.log('[FRONTEND] [AUTH_SLICE] ===== LOGIN SUCCESS =====');
      console.log('[FRONTEND] [AUTH_SLICE] Returning result to component');
      
      return { token, user };
    } catch (error) {
      console.error('\n' + '!'.repeat(80));
      console.error('[FRONTEND] [AUTH_SLICE] ===== LOGIN ERROR IN THUNK =====');
      console.error('[FRONTEND] [AUTH_SLICE] Timestamp:', new Date().toISOString());
      console.error('[FRONTEND] [AUTH_SLICE] Error occurred during login');
      console.error('[FRONTEND] [AUTH_SLICE] Error type:', error?.constructor?.name || typeof error);
      console.error('[FRONTEND] [AUTH_SLICE] Error message:', error?.message);
      console.error('[FRONTEND] [AUTH_SLICE] Error code:', error?.code || 'N/A');
      console.error('[FRONTEND] [AUTH_SLICE] Was request sent?', error.request ? 'YES' : 'NO');
      console.error('[FRONTEND] [AUTH_SLICE] Did we get a response?', error.response ? 'YES' : 'NO');
      
      if (error.response) {
        console.error('[FRONTEND] [AUTH_SLICE] Response error status:', error.response.status);
        console.error('[FRONTEND] [AUTH_SLICE] Response error data:', JSON.stringify(error.response.data, null, 2));
        console.error('[FRONTEND] [AUTH_SLICE] Response error headers:', JSON.stringify(error.response.headers, null, 2));
      } else if (error.request) {
        console.error('[FRONTEND] [AUTH_SLICE] Request was made but no response received');
        console.error('[FRONTEND] [AUTH_SLICE] Request details:', error.request);
      } else {
        console.error('[FRONTEND] [AUTH_SLICE] Error setting up request:', error.message);
      }
      
      console.error('[FRONTEND] [AUTH_SLICE] Full error object:', error);
      console.error('[FRONTEND] [AUTH_SLICE] Error stack:', error?.stack);
      
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      console.error('[FRONTEND] [AUTH_SLICE] Extracted error message:', errorMessage);
      console.error('[FRONTEND] [AUTH_SLICE] Rejecting with value:', errorMessage);
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/auth/me');
      return response.data.user;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get user');
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  localStorage.removeItem('token');
  localStorage.removeItem('loginTimestamp');
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: localStorage.getItem('token'),
    isAuthenticated: !!localStorage.getItem('token'),
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        console.log('[FRONTEND] [AUTH_REDUCER] ===== LOGIN PENDING =====');
        console.log('[FRONTEND] [AUTH_REDUCER] Login pending - setting loading state');
        console.log('[FRONTEND] [AUTH_REDUCER] Previous state:', {
          loading: state.loading,
          isAuthenticated: state.isAuthenticated,
          hasToken: !!state.token,
          hasError: !!state.error
        });
        state.loading = true;
        state.error = null;
        console.log('[FRONTEND] [AUTH_REDUCER] New state: loading = true, error = null');
      })
      .addCase(login.fulfilled, (state, action) => {
        console.log('[FRONTEND] [AUTH_REDUCER] ===== LOGIN FULFILLED =====');
        console.log('[FRONTEND] [AUTH_REDUCER] Login fulfilled - updating state');
        console.log('[FRONTEND] [AUTH_REDUCER] Action payload:', {
          hasToken: !!action.payload.token,
          tokenLength: action.payload.token ? action.payload.token.length : 0,
          hasUser: !!action.payload.user,
          userId: action.payload.user?.id,
          userEmail: action.payload.user?.email
        });
        
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
        
        console.log('[FRONTEND] [AUTH_REDUCER] State updated successfully');
        console.log('[FRONTEND] [AUTH_REDUCER] Final state:', { 
          loading: false,
          isAuthenticated: state.isAuthenticated, 
          hasToken: !!state.token,
          tokenLength: state.token ? state.token.length : 0,
          userId: state.user?.id,
          userEmail: state.user?.email,
          userRole: state.user?.role
        });
        console.log('[FRONTEND] [AUTH_REDUCER] ===== STATE UPDATE COMPLETE =====');
      })
      .addCase(login.rejected, (state, action) => {
        console.error('[FRONTEND] [AUTH_REDUCER] ===== LOGIN REJECTED =====');
        console.error('[FRONTEND] [AUTH_REDUCER] Login rejected - setting error state');
        console.error('[FRONTEND] [AUTH_REDUCER] Action payload (error):', action.payload);
        console.error('[FRONTEND] [AUTH_REDUCER] Action error:', action.error);
        
        state.loading = false;
        state.error = action.payload;
        
        console.error('[FRONTEND] [AUTH_REDUCER] State updated with error');
        console.error('[FRONTEND] [AUTH_REDUCER] Final state:', {
          loading: false,
          isAuthenticated: state.isAuthenticated,
          hasToken: !!state.token,
          error: state.error
        });
      })
      // Get current user
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(getCurrentUser.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        localStorage.removeItem('token');
        localStorage.removeItem('loginTimestamp');
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      });
  },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;


